import React from 'react';
import { Empty } from 'antd';
import { useDispatch } from 'react-redux';
import { HelpInfo, HelpInfos, HelpInfosFilter, ServiceType } from 'types';

import StyledTable from 'components/organisms/styled-table';
import ListModifier from 'components/organisms/list-modifier';
import { useIntl } from 'common/config/i18n';
import { helpInfosActions } from 'common/config/store';
import PaginationBlock from 'components/molecules/pagination';
import { htmlParser } from 'common/utils/html-parser';

type Props = {
  onClickEdit?: (helpInfo: HelpInfo) => void;
  onClickDelete?: (helpInfo: HelpInfo) => void;
  isAdmin: boolean;
  data?: HelpInfos;
  filterValues?: HelpInfosFilter;
  serviceTypes: ServiceType[];
};

const getServiceTypeNameById = (serviceTypes: ServiceType[], id: number) => {
  const serviceType = serviceTypes && serviceTypes.find((serviceType) => serviceType.id === id);
  return serviceType?.serviceTypeName;
};

const HelpInfoList: React.FC<Props> = ({
  onClickDelete,
  onClickEdit,
  isAdmin,
  data,
  filterValues,
  serviceTypes,
}: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const columns = [
    {
      title: t('form', 'MESSAGE_LONG'),
      key: 'longMessage',
      dataIndex: 'longMessage',
      render: (longMessage: string) => {
        return <div className="ql-editor">{htmlParser(longMessage)}</div>;
      },
    },
    {
      title: t('form', 'MESSAGE_COURT'),
      dataIndex: 'shortMessage',
      key: 'shortMessage',
    },
    {
      title: t('form', 'SITUATION'),
      key: 'measures',
      render: (record: HelpInfo) => {
        return record?.situations.map((x, index) => (
          <div style={{ width: '300px' }} key={`id_${x?.id} - index_${index}`}>
            {x?.situationCategory?.name} -{x?.name}
          </div>
        ));
      },
    },
    {
      title: t('form', 'circulationType'),
      render: (record: HelpInfo) => {
        return record?.serviceTypeIDs?.map((id) => {
          const serviceTypeName = getServiceTypeNameById(serviceTypes, id);
          return (
            !!serviceTypeName && (
              <div style={{ width: '300px' }} key={id}>
                {serviceTypeName}
              </div>
            )
          );
        });
      },
    },
    {
      title: t('form', 'SUB_FAMILY'),
      key: 'subFamily',
      dataIndex: 'subFamily',
    },
    {
      title: t('table', 'COLUMN_EDIT_DELETE'),
      key: 'modifier',
      shouldBeAdmin: true,
      render: (record: HelpInfo) => (
        <ListModifier
          hideDeleteButton={!isAdmin}
          hideEditButton={!isAdmin}
          onDelete={() => onClickDelete?.(record)}
          onEdit={() => onClickEdit?.(record)}
        />
      ),
    },
  ];

  const calculatedColumns = isAdmin ? columns : columns.filter((column) => !column.shouldBeAdmin);

  return (
    <>
      <PaginationBlock
        data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
        onChange={(page: number, pageSize?: number) =>
          dispatch(
            helpInfosActions.fetchHelpInfos({
              ...filterValues,
              pageNumber: page,
              maxResultPage: pageSize,
            })
          )
        }
      />
      <StyledTable
        pagination={false}
        dataSource={data?.result ? data?.result : []}
        columns={calculatedColumns}
        rowKey="id"
        textAlign="left"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
      />
      <PaginationBlock
        data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
        onChange={(page: number, pageSize?: number) =>
          dispatch(
            helpInfosActions.fetchHelpInfos({
              ...filterValues,
              pageNumber: page,
              maxResultPage: pageSize,
            })
          )
        }
      />
    </>
  );
};

export default HelpInfoList;
